import React, { useState, useEffect, Fragment, useCallback } from 'react';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { getAllStreams, getOeTaskStatus } from '@lib/api/index';
import { IcoCheck, IcoDanger, IcoTimeForward } from '@icons';
import { columnCentered, columnToRight } from '@lib/helpers/agGridHelpers';
import { dateFormatter } from 'lib/helpers/formatting';
import OESimpleTable from '@components/oetable/OESimpleTable';
import ActionBtnRendererTasks from '@components/actionBtnRenderer/ActionBtnRendererTasks';
import { OETrans } from 'components/translation/OETrans';
import { Loading } from '@components/atomic/atoms';

const Tasks = ({ projectId, canEdit, allStreams, projectStreamConfigurationType, projectAdvancedConfig, isCoordinator }) => {
	const [streams, setStreams] = useState([]);
	const [allTasks, setAllTasks] = useState([]);
	const [columnDefs, setColumnDefs] = useState([]);
	const [refresh, setRefresh] = useState(false);
	const [optionsStatus, setOptionsStatus] = useState([]);
	//console.log('allStreams', allStreams);

	const fetchStreams = useCallback(
		async (streamsParam) => {
			if (streamsParam) {
				setStreams(streamsParam);
				setAllTasks(handleAllTasks(streamsParam));
			} else {
				getAllStreams(projectId, true).then((retrievedStreams) => {
					if (retrievedStreams.success) {
						setStreams(retrievedStreams.data);
						setAllTasks(handleAllTasks(retrievedStreams.data));
					}
				});
			}
		},
		[projectId],
	);

	const handleAllTasks = (allTasks) => {
		return allTasks
			.sort((a, b) => a.streamId - b.streamId)
			.reduce((acc, obj) => {
				const arrayWithProperties = obj.tasks.map((item) => ({
					...item,
					streamId: obj.streamId,
					streamName: obj.name,
				}));
				return acc.concat(arrayWithProperties);
			}, []);
	};

	useEffect(() => {
		fetchStreams(allStreams);

		getOeTaskStatus().then((retrievedTaskStatus) => {
			if (retrievedTaskStatus.success) {
				setOptionsStatus(
					retrievedTaskStatus.data.map((status) => ({
						value: status.code,
						label: status.name,
					})),
				);
			}
		});
	}, [fetchStreams, allStreams]);

	useEffect(() => {
		if (refresh) {
			fetchStreams();
			setRefresh(false);
		}
	}, [refresh, projectId, fetchStreams]);

	const OnlyWarningCellRenderer = useCallback((params) => {
		let hasSubtasks = params?.data?.numSubtasks > 0;
		let icon;
		let message = '';
		//console.log('params', params);

		if (params && params.data) {
			if (hasSubtasks) {
				const isCompleted = params.data.completed;
				const isLate = params.data.late;
				const isWorking = params.data.working;

				if (isCompleted) {
					//spunta verde
					message = 'Subtask completati';
					icon = <IcoCheck style={{ fill: '#689689' }} />;
				} else if (isLate) {
					//icona warning
					message = 'Subtask in ritardo';
					icon = <IcoDanger />;
				} else if (isWorking) {
					//icona in corso
					message = 'Subtask in corso o sospesi';
					icon = <IcoTimeForward />;
				}
			} else {
				const currentDate = new Date().toISOString();
				const taskStartDate = new Date(params.data.startDate).toISOString();
				const taskEndDate = new Date(params.data.endDate).toISOString();

				if (currentDate >= taskStartDate && currentDate <= taskEndDate) {
					//icona in corso
					message = 'Task in corso';
					icon = <IcoTimeForward />;
				} else if (currentDate > taskEndDate) {
					//spunta verde
					message = 'Task completato';
					icon = <IcoCheck style={{ fill: '#689689' }} />;
				}
			}

			return (
				<>
					<Row className="align-items-center justify-content-center h-100 flex-nowrap">
						<Col className="col-auto">
							<OverlayTrigger placement="top" overlay={<Tooltip>{message}</Tooltip>}>
								<span>{icon}</span>
							</OverlayTrigger>
						</Col>
					</Row>
				</>
			);
		}
	}, []);

	useEffect(() => {
		setColumnDefs([
			{
				headerName: 'Stato',
				field: 'completed',
				cellRenderer: OnlyWarningCellRenderer,
				cellRendererParams: {
					queryParam: 'completed',
				},
				minWidth: 120,
				maxWidth: 120,
				...columnCentered(),
			},
			{
				headerName: 'WP di riferimento',
				field: 'streamId',
				filter: 'agTextColumnFilter',
				flex: 1,
				valueGetter: (params) => {
					return params.data.streamId + ' - ' + params.data.streamName;
				},
			},
			{
				headerName: 'Denominazione Task',
				field: 'name',
				filter: 'agTextColumnFilter',
				flex: 2,
			},
			{
				headerName: 'N. Subtasks',
				field: 'numSubtasks',
				filter: 'agTextColumnFilter',
				flex: 1,
				maxWidth: 150,
				...columnToRight(),
			},
			{
				headerName: 'Responsabile',
				field: 'responsibles',
				filter: 'agTextColumnFilter',
				flex: 2,
				valueGetter: (params) => {
					if (!params.data.responsibles.length) return '';
					return params.data.responsibles.map((responsible) => `${responsible.name}`).join(', ');
				},
			},
			{
				headerName: 'Inizio attività',
				field: 'startDate',
				flex: 2,
				valueGetter: (params) => {
					if (!params.data.startDate) return '';
					return dateFormatter(params.data.startDate);
				},
				...columnCentered(),
				minWidth: 160,
				maxWidth: 160,
			},
			{
				headerName: 'Mese inizio',
				field: 'monthsStartDate',
				valueGetter: (params) => {
					if (!params.data.monthsStartDate) return '';
					return 'M' + params.data.monthsStartDate;
				},
				...columnCentered(),
			},
			{
				headerName: 'Fine attività',
				field: 'endDate',
				flex: 2,
				valueGetter: (params) => {
					if (!params.data.endDate) return '';
					return dateFormatter(params.data.endDate);
				},
				...columnCentered(),
				minWidth: 160,
				maxWidth: 160,
			},
			{
				headerName: 'Mese fine',
				field: 'monthsEndDate',
				valueGetter: (params) => {
					if (!params.data.monthsEndDate) return '';
					return 'M' + params.data.monthsEndDate;
				},
				...columnCentered(),
			},
			{
				headerName: '',
				minWidth: 90,
				maxWidth: 90,
				cellRenderer: ActionBtnRendererTasks,
				cellRendererParams: {
					setRefresh: setRefresh,
					projectId: projectId,
					canEdit: canEdit,
					optionsStatus: optionsStatus,
					streams: streams,
					projectStreamConfigurationType: projectStreamConfigurationType,
					projectAdvancedConfig: projectAdvancedConfig,
					isCoordinator: isCoordinator,
				},
				resizable: false,
				cellStyle: { textAlign: 'center', padding: 0 },
			},
		]);
	}, [
		canEdit,
		optionsStatus,
		projectId,
		streams,
		OnlyWarningCellRenderer,
		projectStreamConfigurationType,
		projectAdvancedConfig,
		isCoordinator,
	]);

	if (!Array.isArray(allTasks)) return <Loading fixedHeight="50vh" />;

	return (
		<>
			{allTasks.length ? (
				<>
					<Row>
						<OESimpleTable columnDefs={columnDefs} rowData={allTasks} buttonToolbarVisible={true} pageSizeId={'tasks'} fullWidth={true} />
					</Row>
				</>
			) : (
				<Row>
					<Col className="d-flex justify-content-center align-items-center">
						<OETrans code="common.noitems" fallback={'Nessun elemento disponibile'} />
					</Col>
				</Row>
			)}
		</>
	);
};
export default Tasks;
